import { ReactElement, ReactNode, useEffect } from 'react'
import produce from 'immer'
import { cloneDeep, find, findIndex, reject } from 'lodash-es'
import { Vector3 } from 'three'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import { getModelBoundingBox } from '@editor/utils'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { roofStoreyKey } from '@editorStores'
import { useStoreReinitializer } from 'src/state/hooks'
import {
  availableStoreysFromBoundaries,
  excludeStoreyFromVisibilitySets,
  flattenDomains,
  includeStoreyInVisibilitySets,
  mergeStoreyFilters,
  prepareModelForStore,
} from './utils'

const allElementTypesMap: Record<ElementTypes, ElementTypes> = {
  beams: 'beams',
  columns: 'columns',
  inner_walls: 'inner_walls',
  lintels: 'lintels',
  outer_walls: 'outer_walls',
  purlins: 'purlins',
  roof_slabs: 'roof_slabs',
  rips: 'rips',
  slabs: 'slabs',
  vertical_roof_slabs: 'vertical_roof_slabs',
  vertical_slabs: 'vertical_slabs',
  slab_beams: 'slab_beams',
  roof_slab_beams: 'roof_slab_beams',
  standard_rips: 'standard_rips',
  foundation: 'foundation',
}

const allElementTypes = Object.keys(allElementTypesMap) as ElementTypes[]

interface InteractableTypeConfig {
  uuid: string
  interactableByType: InteractableByType
}

interface Props {
  children: ReactNode
  model: PlanarModel
  refreshAt: string | number
  project: ProjectWithLoadInfo
}

interface ServerState {
  model: PlanarModel
  project: ProjectWithLoadInfo
}
interface ModelStoreInitialState {
  model: PlanarModel
  initialModelCenter: [number, number, number]
  initialModelBoundingBox: {
    min: [number, number, number]
    max: [number, number, number]
  }
  domains: Domain[]
  translucent: boolean
  showComments: boolean
  showWallOpenings: boolean
  showVerticalOpeningAlignmentHelpers: boolean
  showPlanArea: boolean
  /** Render floorplan walls with their real assembly thickness instead of a single centerline. */
  showRealWallThickness: boolean
  availableStoreys: Set<string>
  visibleStoreys: Set<string>
  visibleDomains: Set<string>
  visibilityByType: VisibilityByType
  visibilityByTypeAndStorey: VisibilityByTypeAndStorey
  /**
   * Snapshot of `visibilityByType` for the compact-hidden types, taken when the "compact" model
   * column is enabled (so they can be restored when it is disabled again). `null` while not compact.
   */
  compactHiddenTypeVisibilitySnapshot: Partial<Record<ElementTypes, boolean>> | null
  interactableByTypeConfigs: InteractableTypeConfig[]
  blockingElements: BlockingByType
  positionVisibilityFollowsWallVisibility: boolean
  slabPositionVisibilityFollowsSlabVisibility: boolean
  roofSlabPositionVisibilityFollowsRoofSlabVisibility: boolean
  labelVisibilityModel: LabelVisibilityModel
  transparencyVisibilityModel: TransparencyVisibilityModel
  project: ProjectWithLoadInfo
}

interface LabelVisibilityModel {
  visibilityByType: VisibilityByType
}

interface TransparencyVisibilityModel {
  visibilityByType: VisibilityByType
  opacity: number
  guidToTransparent: Record<string, boolean>
}

interface ModelStoreType extends ModelStoreInitialState {
  setTranslucent: (translucent: boolean) => void
  setShowComments: (show: boolean) => void
  setShowWallOpenings: (show: boolean) => void
  setShowVerticalOpeningAlignmentHelpers: (show: boolean) => void
  setShowPlanArea: (show: boolean) => void
  setShowRealWallThickness: (show: boolean) => void
  toggleAllStoreysVisibility: () => void
  setStoreyVisibility: (storey: string, visibility: boolean) => void
  includeStoreyInVisibility: (storey: string) => void
  excludeStoreyFromVisibility: (storey: string) => void
  setAllStoreysInvisible: () => void
  toggleSingleStoreyVisibility: (storey: string) => void
  setVisibleStoreys: (storeys: string[]) => void
  setVisibleDomains: (domains: string[]) => void
  setTypeVisibility: setTypeAction
  toggleTypeVisibility: toggleTypeAction
  setTypeVisibilityByStorey: setTypeVisibilityByStoreyAction
  toggleTypeVisibilityByStorey: toggleTypeVisibilityByStoreyAction
  setCompactTypesHidden: (hidden: boolean) => void
  addInteractableByTypeConfig: (config: InteractableTypeConfig) => void
  removeInteractableByTypeConfig: (uuid: string) => void
  setBlockingElements: (blockingElements: BlockingByType) => void
  setBlockingElement: (type: ElementTypes, blocking: boolean) => void
  resetBlockingElements: () => void
  setPositionVisibilityFollowsWallVisibility: (follows: boolean) => void
  setSlabPositionVisibilityFollowsSlabVisibility: (follows: boolean) => void
  setRoofSlabPositionVisibilityFollowsRoofSlabVisibility: (follows: boolean) => void
  setLabelVisibilityByType: (type: ElementTypes, visible: boolean) => void
  setLabelVisibilityForTypes: (visibleTypes: ElementTypes[]) => void
  setTransparencyVisibilityForTypes: (visibleTypes: ElementTypes[]) => void
  setTransparencyOpacity: (opacity: number) => void
  setTransparencyForGuid: (guid: string, transparent: boolean) => void
  setTransparencyForGuids: (guids: string[], transparent: boolean) => void
  clearTransparencyVisibilityModel: () => void
  focusElements: (elementGuids: string[]) => void
  addSlab: (slab: ShapeObject) => void
  addVerticalRoofSlab: (roof: ShapeObject) => void
  addBeam: (beam: PlanLineElement) => void
  addWall: (wall: Wall) => void
  addOpening: (wallGuid: string, opening: Opening) => void
  addPurlin: (purlin: PlanLineElement) => void
  addColumn: (beam: PlanLineElement) => void
  addRip: (rip: Rip) => void
  addLintel: (lintel: Lintel) => void
  updateBeam: (beam: { guid: string; shape: LineShape }) => void
  updateWall: (wall: ShapeObject) => void
  updateOpening: (wallGuid: string, opening: Opening) => void
  updatePurlin: (purlin: { guid: string; shape: LineShape }) => void
  updateColumn: (column: { guid: string; shape: LineShape }) => void
  updateVerticalSlab: (slab: { guid: string; shape: PolygonShape }) => void
  updateVerticalRoof: (slab: { guid: string; shape: PolygonShape }) => void
  updateVerticalSlabMetadata: (guid: string, metadata: VerticalSlabMetadata) => void
  updateSlab: (slab: { guid: string; shape: PolygonShape }) => void
  updateRoof: (slab: { guid: string; shape: PolygonShape }) => void
  updateFoundation: (slab: { guid: string; shape: PolygonShape }) => void
  updateRip: (rip: Rip) => void
  updateLintel: (lintel: Partial<Lintel>) => void
  updateSlabBeam: (slabGuid: string, slabBeam: SlabBeam) => void
  removeVerticalSlab: (slabGuid: string) => void
  removeBeam: (beamGuid: string) => void
  removeOpening: (wallGuid: string, openingGuid: string) => void
  removeColumn: (columnGuid: string) => void
  removePurlin: (columnGuid: string) => void
  removeVerticalRoofSlab: (roofGuid: string) => void
  removeWall: (wallGuid: string) => void
  removeRip: (guid: string) => void
  removeLintel: (guid: string) => void
  rejectLocalElements: () => void
  // read only getters
  hasElement: (guid: string) => boolean
  getVerticalRoofSlab: (guid: string) => VerticalRoofSlab | undefined
  updateStorey: (guid: string, storey: string) => void

  clear: () => void
  updateProjectLoadInfo: (loadInfo: LoadInfo) => void

  replaceRips: (rips: Rip[]) => void
}

const byTypeInitialState = (enabled = true) =>
  allElementTypes.reduce(
    (collector, type) => ({ ...collector, [type]: enabled }),
    {} as VisibilityByType,
  )

// Element types whose storey-controls toggles disappear when the "compact" model column is enabled.
// While compact these are hidden in the scene too (so nothing stays visible that the user can no
// longer toggle) and restored to their previous visibility when compact is disabled again.
const compactHiddenTypes: ElementTypes[] = [
  'vertical_roof_slabs',
  'roof_slab_beams',
  'purlins',
  'vertical_slabs',
  'beams',
  'columns',
  'lintels',
  'rips',
  'slab_beams',
]

const defaultFoundation = {
  guid: 'foundation-replacement',
  element_guid: 'foundation-replacement',
  element_type: 'foundation' as ElementTypes,
  label: 'BP01',
  storey: '0',
  shape: {
    points: [
      new ImmutableVector3(-10, -10, 0),
      new ImmutableVector3(10, -10, 0),
      new ImmutableVector3(10, 10, 0),
      new ImmutableVector3(-10, 10, 0),
    ],
  },
} as Foundation

const initialState: ModelStoreInitialState = {
  model: {
    walls: [],
    slabs: [],
    roof_slabs: [],
    vertical_slabs: [],
    vertical_roof_slabs: [],
    beams: [],
    columns: [],
    purlins: [],
    rips: [],
    lintels: [],
    foundationElements: [defaultFoundation],
    storey_boundaries: {},
  },
  initialModelCenter: [0, 0, 0],
  initialModelBoundingBox: {
    min: [0, 0, 0],
    max: [0, 0, 0],
  },
  domains: [],
  translucent: false,
  showComments: true,
  showWallOpenings: false,
  showVerticalOpeningAlignmentHelpers: true,
  showPlanArea: false,
  showRealWallThickness: false,
  availableStoreys: new Set(),
  visibleStoreys: new Set(),
  visibleDomains: new Set(),
  visibilityByType: {
    ...byTypeInitialState(),
    foundation: false,
    slab_beams: false,
    roof_slab_beams: false,
  },
  visibilityByTypeAndStorey: allElementTypes.reduce(
    (collector, type) => ({ ...collector, [type]: new Set() }),
    {} as VisibilityByTypeAndStorey,
  ),
  compactHiddenTypeVisibilitySnapshot: null,
  interactableByTypeConfigs: [],
  blockingElements: byTypeInitialState(false),
  positionVisibilityFollowsWallVisibility: true,
  slabPositionVisibilityFollowsSlabVisibility: true,
  roofSlabPositionVisibilityFollowsRoofSlabVisibility: true,
  labelVisibilityModel: {
    visibilityByType: {
      ...byTypeInitialState(false),
      beams: true,
      purlins: true,
      columns: true,
      vertical_slabs: true,
    },
  },
  transparencyVisibilityModel: {
    visibilityByType: byTypeInitialState(true),
    opacity: 0.1,
    guidToTransparent: {},
  },
  // will be set in createInitialState
  project: {} as ProjectWithLoadInfo,
}

const createInitialState = ({ model: parsingResult, project }: ServerState) => {
  const model = prepareModelForStore(parsingResult)

  // we create an model center for the model in order to use it for the camera event
  // and avoid setting camera to the center of the model on every model change
  let modelCenter: [number, number, number] = [0, 0, 0]

  const box = getModelBoundingBox(model)

  const center = box.getCenter(new Vector3())

  modelCenter = center.toArray()
  // This is a workaround to prevent the camera from
  // looking from a weird angle from above
  if (center.x === 0 && center.y === 0) {
    modelCenter = [0.01, 0.01, 0]
  }

  // This is needed because in the floorplan drawing we have objects far outside the model
  const pointFarOutside = new Vector3(center.x, center.y, box.max.z + 500)
  box.expandByPoint(pointFarOutside)
  const initialModelBoundingBox = { min: box.min.toArray(), max: box.max.toArray() }

  const availableStoreys = availableStoreysFromBoundaries(model.storey_boundaries)
  const availableNonRoofStoreys = Array.from(availableStoreys).filter(
    storey => storey !== roofStoreyKey,
  )
  // Initialize visibilityByTypeAndStorey with all storeys visible for each element type
  const visibilityByTypeAndStorey = allElementTypes.reduce(
    (collector, type) => ({
      ...collector,
      [type]: new Set(availableNonRoofStoreys),
    }),
    {} as VisibilityByTypeAndStorey,
  )

  return {
    model,
    domains: flattenDomains(model),
    availableStoreys,
    visibleStoreys: new Set(availableStoreys),
    visibilityByTypeAndStorey,
    project,
    initialModelCenter: modelCenter,
    initialModelBoundingBox,
  }
}

const storeyFiltersOf = (state: ModelStoreInitialState) => ({
  availableStoreys: state.availableStoreys,
  visibleStoreys: state.visibleStoreys,
  visibilityByTypeAndStorey: state.visibilityByTypeAndStorey,
  storeyBoundaries: state.model.storey_boundaries,
})

const createStore = () =>
  create<ModelStoreType>(
    combine(cloneDeep(initialState), (set, get) => ({
      clear: () => set(cloneDeep(initialState)),

      setTranslucent: (translucent: boolean) => {
        set(
          produce(state => {
            state.translucent = translucent
          }),
        )
      },

      setShowComments: (show: boolean) =>
        set(
          produce(state => {
            state.showComments = show
          }),
        ),

      setShowWallOpenings: (show: boolean) =>
        set(
          produce(state => {
            state.showWallOpenings = show
          }),
        ),

      setShowVerticalOpeningAlignmentHelpers: (show: boolean) =>
        set(
          produce(state => {
            state.showVerticalOpeningAlignmentHelpers = show
          }),
        ),

      setShowPlanArea: (show: boolean) =>
        set(
          produce(state => {
            state.showPlanArea = show
          }),
        ),

      setShowRealWallThickness: (show: boolean) =>
        set(
          produce(state => {
            state.showRealWallThickness = show
          }),
        ),

      toggleAllStoreysVisibility: () => {
        set(state => ({
          visibleStoreys: new Set(
            state.availableStoreys.size === state.visibleStoreys.size ? [] : state.availableStoreys,
          ),
        }))
      },

      toggleSingleStoreyVisibility: (storey: string) => {
        set(
          produce(state => {
            state.visibleStoreys.has(storey)
              ? state.visibleStoreys.delete(storey)
              : state.visibleStoreys.add(storey)
          }),
        )
      },

      setAllStoreysInvisible: () => {
        set(
          produce(state => {
            state.visibleStoreys = new Set()
          }),
        )
      },

      setVisibleStoreys: (storeys: string[]) => {
        set(
          produce(state => {
            state.visibleStoreys = new Set(storeys)
          }),
        )
      },

      setStoreyVisibility: (storey: string, visibility: boolean) => {
        set(
          produce(state => {
            // its a set, so adding elements is always duplicate free
            visibility ? state.visibleStoreys.add(storey) : state.visibleStoreys.delete(storey)
          }),
        )
      },

      includeStoreyInVisibility: (storey: string) => {
        set(
          produce(state => {
            // Known before the refetch, so the refetch keeps the user's choice for it.
            state.availableStoreys.add(storey)
            includeStoreyInVisibilitySets(
              state.visibleStoreys,
              state.visibilityByTypeAndStorey,
              storey,
            )
          }),
        )
      },

      excludeStoreyFromVisibility: (storey: string) => {
        set(
          produce(state => {
            state.availableStoreys.delete(storey)
            excludeStoreyFromVisibilitySets(
              state.visibleStoreys,
              state.visibilityByTypeAndStorey,
              storey,
            )
          }),
        )
      },

      setVisibleDomains: (domainGuids: string[]) =>
        set(
          produce(state => {
            state.visibleDomains = new Set(domainGuids)
          }),
        ),

      setTypeVisibility: (type: ElementTypes, visible = true) =>
        set(
          produce(state => {
            state.visibilityByType[type] = visible
          }),
        ),

      toggleTypeVisibility: (type: ElementTypes) =>
        set(
          produce(state => {
            state.visibilityByType[type] = !state.visibilityByType[type]
          }),
        ),

      setTypeVisibilityByStorey: (type: ElementTypes, storeys: string[]) =>
        set(
          produce(state => {
            state.visibilityByTypeAndStorey[type] = new Set(storeys)
          }),
        ),

      toggleTypeVisibilityByStorey: (type: ElementTypes, storey: string) =>
        set(
          produce(state => {
            const currentSet = state.visibilityByTypeAndStorey[type]
            const availableStoreysArray = Array.from(state.availableStoreys) as string[]
            const availableNonRoofStoreys = availableStoreysArray.filter(s => s !== roofStoreyKey)

            let newSet: Set<string>
            if (!currentSet) {
              // If undefined, all storeys are visible. Toggle means hide this storey.
              newSet = new Set(availableNonRoofStoreys.filter(s => s !== storey))
            } else {
              // Toggle the storey in the existing set
              newSet = new Set(currentSet)
              if (newSet.has(storey)) {
                newSet.delete(storey)
              } else {
                newSet.add(storey)
              }
            }

            state.visibilityByTypeAndStorey[type] = newSet
          }),
        ),

      setCompactTypesHidden: (hidden: boolean) =>
        set(
          produce(state => {
            if (hidden) {
              // Already hidden (snapshot taken) — don't overwrite it with the now-hidden values.
              if (state.compactHiddenTypeVisibilitySnapshot) return

              const snapshot: Partial<Record<ElementTypes, boolean>> = {}
              compactHiddenTypes.forEach(type => {
                snapshot[type] = state.visibilityByType[type]
                state.visibilityByType[type] = false
              })
              state.compactHiddenTypeVisibilitySnapshot = snapshot
            } else {
              const snapshot = state.compactHiddenTypeVisibilitySnapshot
              if (!snapshot) return

              compactHiddenTypes.forEach(type => {
                const previous = snapshot[type]
                if (previous !== undefined) state.visibilityByType[type] = previous
              })
              state.compactHiddenTypeVisibilitySnapshot = null
            }
          }),
        ),

      addInteractableByTypeConfig: (config: InteractableTypeConfig) =>
        set(state => ({
          interactableByTypeConfigs: [...state.interactableByTypeConfigs, config],
        })),

      removeInteractableByTypeConfig: (uuid: string) =>
        set(state => ({
          interactableByTypeConfigs: reject(state.interactableByTypeConfigs, ['uuid', uuid]),
        })),

      setBlockingElements: (blockingElements: BlockingByType) => set({ blockingElements }),
      resetBlockingElements: () =>
        set({
          blockingElements: byTypeInitialState(false),
        }),
      setBlockingElement: (type: ElementTypes, blocking: boolean) =>
        set(
          produce(state => {
            state.blockingElements[type] = blocking
          }),
        ),

      setPositionVisibilityFollowsWallVisibility: (follows: boolean) =>
        set(
          produce(state => {
            state.positionVisibilityFollowsWallVisibility = follows
          }),
        ),

      setSlabPositionVisibilityFollowsSlabVisibility: (follows: boolean) =>
        set(
          produce(state => {
            state.slabPositionVisibilityFollowsSlabVisibility = follows
          }),
        ),

      setRoofSlabPositionVisibilityFollowsRoofSlabVisibility: (follows: boolean) =>
        set(
          produce(state => {
            state.roofSlabPositionVisibilityFollowsRoofSlabVisibility = follows
          }),
        ),

      setLabelVisibilityByType: (type: ElementTypes, visible: boolean) =>
        set(
          produce(state => {
            state.labelVisibilityModel.visibilityByType[type] = visible
          }),
        ),

      setLabelVisibilityForTypes: (visibleTypes: ElementTypes[]) =>
        set(
          produce(state => {
            const updatedVisibility = byTypeInitialState(false)
            visibleTypes.forEach(type => {
              updatedVisibility[type] = true
            })
            state.labelVisibilityModel.visibilityByType = updatedVisibility
          }),
        ),

      setTransparencyVisibilityForTypes: (visibleTypes: ElementTypes[]) =>
        set(
          produce(state => {
            const updatedVisibility = byTypeInitialState(false)
            visibleTypes.forEach(type => {
              updatedVisibility[type] = true
            })
            state.transparencyVisibilityModel.visibilityByType = updatedVisibility
          }),
        ),

      setTransparencyOpacity: (opacity: number) =>
        set(
          produce(state => {
            state.transparencyVisibilityModel.opacity = opacity
          }),
        ),

      setTransparencyForGuid: (guid: string, transparent: boolean) =>
        set(
          produce(state => {
            if (transparent) {
              state.transparencyVisibilityModel.guidToTransparent[guid] = true
            } else {
              delete state.transparencyVisibilityModel.guidToTransparent[guid]
            }
          }),
        ),

      setTransparencyForGuids: (guids: string[], transparent: boolean) =>
        set(
          produce(state => {
            guids.forEach(guid => {
              if (transparent) {
                state.transparencyVisibilityModel.guidToTransparent[guid] = true
              } else {
                delete state.transparencyVisibilityModel.guidToTransparent[guid]
              }
            })
          }),
        ),

      clearTransparencyVisibilityModel: () =>
        set(
          produce(state => {
            // Reset all type visibilities to true
            state.transparencyVisibilityModel.visibilityByType = byTypeInitialState(true)
            // Clear all entries in guidToTransparent
            state.transparencyVisibilityModel.guidToTransparent = {}
          }),
        ),

      focusElements: (elementGuids: string[]) =>
        set(
          produce(state => {
            // Clear transparency store
            state.transparencyVisibilityModel.visibilityByType = byTypeInitialState(false)
            state.transparencyVisibilityModel.guidToTransparent = {}
            // Enable translucent mode
            state.translucent = true
            // Set all element types to transparent (false = transparent)
            state.transparencyVisibilityModel.visibilityByType = byTypeInitialState(true)
            // Set provided elements to NOT transparent (false = not transparent) - these will stand out
            elementGuids.forEach(guid => {
              state.transparencyVisibilityModel.guidToTransparent[guid] = false
            })
          }),
        ),

      addVerticalRoofSlab: (roof: ShapeObject) =>
        set(
          produce(state => {
            state.model.vertical_roof_slabs.push(roof)
          }),
        ),

      addColumn: (column: PlanLineElement) =>
        set(
          produce(state => {
            state.model.columns.push(column)
          }),
        ),

      addSlab: (slab: ShapeObject) =>
        set(
          produce(state => {
            state.model.vertical_slabs.push(slab)
          }),
        ),

      addBeam: (beam: PlanLineElement) =>
        set(
          produce(state => {
            state.model.beams.push(beam)
          }),
        ),

      addWall: (wall: Wall) =>
        set(
          produce(state => {
            state.model.walls.push(wall)
          }),
        ),

      addOpening: (wallGuid: string, opening: Opening) =>
        set(
          produce(state => {
            const index = findIndex(state.model.walls, ['guid', wallGuid])

            if (index === -1) return

            const wall = state.model.walls[index]

            wall.openings = [...(wall.openings || []), opening]

            state.model.walls[index] = {
              ...state.model.walls[index],
              ...wall,
            }
          }),
        ),

      addPurlin: (purlin: PlanLineElement) =>
        set(
          produce(state => {
            state.model.purlins.push(purlin)
          }),
        ),

      addRip: (rip: Rip) =>
        set(
          produce(state => {
            state.model.rips.push(rip)
          }),
        ),

      addLintel: (lintel: Lintel) =>
        set(
          produce(state => {
            state.model.lintels.push(lintel)
          }),
        ),

      updateSlab: (slab: { guid: string; shape: PolygonShape }) =>
        set(
          produce(state => {
            const index = findIndex(state.model.slabs, ['guid', slab.guid])

            if (index === -1) return

            state.model.slabs[index] = {
              ...state.model.slabs[index],
              ...slab,
            }
          }),
        ),

      updateFoundation: (slab: { guid: string; shape: PolygonShape }) =>
        set(
          produce(state => {
            const index = findIndex(state.model.foundationElements, ['guid', slab.guid])

            if (index === -1) {
              state.model.foundationElements.push({
                ...slab,
                element_guid: slab.guid,
                element_type: 'foundation',
              } as Foundation)
              return
            }

            state.model.foundationElements[index] = {
              ...state.model.foundationElements[index],
              ...slab,
            }
          }),
        ),

      updateRoof: (roof: { guid: string; shape: PolygonShape }) =>
        set(
          produce(state => {
            const index = findIndex(state.model.roof_slabs, ['guid', roof.guid])

            if (index === -1) return

            state.model.roof_slabs[index] = {
              ...state.model.roof_slabs[index],
              ...roof,
            }
          }),
        ),

      updateStorey: (guid: string, storey: string) =>
        set(
          produce(state => {
            const verticalRoofIndex = findIndex(state.model.vertical_roof_slabs, ['guid', guid])

            if (verticalRoofIndex !== -1) {
              state.model.vertical_roof_slabs[verticalRoofIndex] = {
                ...state.model.vertical_roof_slabs[verticalRoofIndex],
                storey,
              }
              return
            }

            const verticalSlabIndex = findIndex(state.model.vertical_slabs, ['guid', guid])

            if (verticalSlabIndex !== -1) {
              state.model.vertical_slabs[verticalSlabIndex] = {
                ...state.model.vertical_slabs[verticalSlabIndex],
                storey,
              }
            }
          }),
        ),

      updateVerticalSlab: (slab: { guid: string; shape: PolygonShape }) =>
        set(
          produce(state => {
            const index = findIndex(state.model.vertical_slabs, ['guid', slab.guid])

            if (index === -1) return

            state.model.vertical_slabs[index] = {
              ...state.model.vertical_slabs[index],
              ...slab,
            }
          }),
        ),

      updateVerticalSlabMetadata: (guid: string, metadata: VerticalSlabMetadata) =>
        set(
          produce(state => {
            const index = findIndex(state.model.vertical_slabs, ['guid', guid])

            if (index === -1) return

            state.model.vertical_slabs[index] = {
              ...state.model.vertical_slabs[index],
              metadata: {
                ...state.model.vertical_slabs[index].metadata,
                ...metadata,
              },
            }
          }),
        ),

      updateVerticalRoof: (roof: { guid: string; shape: PolygonShape }) =>
        set(
          produce(state => {
            const index = findIndex(state.model.vertical_roof_slabs, ['guid', roof.guid])

            if (index === -1) return

            state.model.vertical_roof_slabs[index] = {
              ...state.model.vertical_roof_slabs[index],
              ...roof,
            }
          }),
        ),

      updateColumn: (column: { guid: string; shape: LineShape }) =>
        set(
          produce(state => {
            const index = findIndex(state.model.columns, ['guid', column.guid])

            if (index === -1) return

            state.model.columns[index] = {
              ...state.model.columns[index],
              ...column,
            }
          }),
        ),

      updateBeam: (beam: { guid: string; shape: LineShape }) =>
        set(
          produce(state => {
            const index = findIndex(state.model.beams, ['guid', beam.guid])

            if (index === -1) return

            state.model.beams[index] = {
              ...state.model.beams[index],
              ...beam,
            }
          }),
        ),

      updateWall: (wall: ShapeObject) =>
        set(
          produce(state => {
            const index = findIndex(state.model.walls, ['guid', wall.guid])

            if (index === -1) return

            state.model.walls[index] = {
              ...state.model.walls[index],
              ...wall,
            }
          }),
        ),

      updateOpening: (wallGuid: string, opening: Opening) =>
        set(
          produce(state => {
            const wallIndex = findIndex(state.model.walls, ['guid', wallGuid])

            if (wallIndex === -1) return

            const wall = state.model.walls[wallIndex]
            const openingIndex = findIndex(wall.openings, { guid: opening.guid })

            if (openingIndex === -1) return

            wall.openings[openingIndex] = {
              ...wall.openings[openingIndex],
              ...opening,
            }

            state.model.walls[wallIndex] = {
              ...state.model.walls[wallIndex],
              ...wall,
            }
          }),
        ),

      updatePurlin: (purlin: { guid: string; shape: LineShape }) =>
        set(
          produce(state => {
            const index = findIndex(state.model.purlins, ['guid', purlin.guid])

            if (index === -1) return

            state.model.purlins[index] = {
              ...state.model.purlins[index],
              ...purlin,
            }
          }),
        ),

      updateRip: (rip: Rip) =>
        set(
          produce(state => {
            const index = findIndex(state.model.rips, { position_guid: rip.position_guid })

            if (index === -1) return

            state.model.rips[index] = {
              ...state.model.rips[index],
              ...rip,
            }
          }),
        ),

      updateLintel: (lintel: Partial<Lintel>) =>
        set(
          produce(state => {
            const index = findIndex(state.model.lintels, { position_guid: lintel.position_guid })

            if (index === -1) return

            state.model.lintels[index] = {
              ...state.model.lintels[index],
              ...lintel,
            }
          }),
        ),

      updateSlabBeam: (slabGuid: string, slabBeam: SlabBeam) =>
        set(
          produce(state => {
            const slabIndex = findIndex(state.model.vertical_slabs, { guid: slabGuid })

            if (slabIndex !== -1) {
              state.model.vertical_slabs[slabIndex] = {
                ...state.model.vertical_slabs[slabIndex],
                beam: {
                  ...state.model.vertical_slabs[slabIndex].beam,
                  ...slabBeam,
                },
              }
            }

            const roofSlabIndex = findIndex(state.model.vertical_roof_slabs, { guid: slabGuid })

            if (roofSlabIndex !== -1) {
              state.model.vertical_roof_slabs[roofSlabIndex] = {
                ...state.model.vertical_roof_slabs[roofSlabIndex],
                beam: {
                  ...state.model.vertical_roof_slabs[roofSlabIndex].beam,
                  ...slabBeam,
                },
              }
            }
          }),
        ),

      removeVerticalSlab: slabGuid =>
        set(
          produce(state => {
            state.model.vertical_slabs = reject(state.model.vertical_slabs, ['guid', slabGuid])
          }),
        ),

      removeWall: wallGuid =>
        set(
          produce(state => {
            state.model.walls = reject(state.model.walls, ['guid', wallGuid])
          }),
        ),

      removeBeam: beamGuid =>
        set(
          produce(state => {
            state.model.beams = reject(state.model.beams, ['guid', beamGuid])
          }),
        ),

      removeOpening: (wallGuid, openingGuid) =>
        set(
          produce(state => {
            const index = findIndex(state.model.walls, ['guid', wallGuid])

            if (index === -1) return

            const wall = state.model.walls[index]

            wall.openings = reject(wall.openings, { guid: openingGuid })

            state.model.walls[index] = {
              ...state.model.walls[index],
              ...wall,
            }
          }),
        ),

      removeColumn: columnGuid =>
        set(
          produce(state => {
            state.model.columns = reject(state.model.columns, ['guid', columnGuid])
          }),
        ),

      removePurlin: purlinGuid =>
        set(
          produce(state => {
            state.model.purlins = reject(state.model.purlins, ['guid', purlinGuid])
          }),
        ),

      removeVerticalRoofSlab: roofGuid =>
        set(
          produce(state => {
            state.model.vertical_roof_slabs = reject(state.model.vertical_roof_slabs, [
              'guid',
              roofGuid,
            ])
          }),
        ),

      removeRip: (guid: string) => {
        set(
          produce(state => {
            state.model.rips = reject(state.model.rips, { position_guid: guid })
          }),
        )
      },

      removeLintel: (guid: string) => {
        set(
          produce(state => {
            state.model.lintels = reject(state.model.lintels, { position_guid: guid })
          }),
        )
      },
      rejectLocalElements: () => {
        set(
          produce(state => {
            // Update state.model to reject elements with { is_local: true }
            state.model.walls = reject(state.model.walls, { is_local: true })
            state.model.slabs = reject(state.model.slabs, { is_local: true })
            state.model.roof_slabs = reject(state.model.roof_slabs, { is_local: true })
            state.model.vertical_slabs = reject(state.model.vertical_slabs, { is_local: true })
            state.model.vertical_roof_slabs = reject(state.model.vertical_roof_slabs, {
              is_local: true,
            })
            state.model.beams = reject(state.model.beams, { is_local: true })
            state.model.columns = reject(state.model.columns, { is_local: true })
            state.model.purlins = reject(state.model.purlins, { is_local: true })
            state.model.rips = reject(state.model.rips, { is_local: true })
            state.model.lintels = reject(state.model.lintels, { is_local: true })
          }),
        )
      },
      replaceRips: (rips: Rip[]) => {
        set(
          produce(state => {
            state.model.rips = rips
          }),
        )
      },
      hasElement: (guid: string) => {
        const state = get()
        return !!find(
          [
            ...state.model.walls,
            ...state.model.slabs,
            ...state.model.roof_slabs,
            ...state.model.vertical_slabs,
            ...state.model.vertical_roof_slabs,
            ...state.model.beams,
            ...state.model.columns,
            ...state.model.purlins,
            ...state.model.rips,
            ...state.model.lintels,
          ],
          { guid },
        )
      },
      getVerticalRoofSlab: (guid: string) => {
        const state = get()
        return find(state.model.vertical_roof_slabs, { guid })
      },

      updateProjectLoadInfo: (loadInfo: LoadInfo) => {
        const state = get()
        set({
          project: {
            ...state.project,
            load_info: loadInfo,
          },
        })
      },
    })),
  )

const useModelStore = createStore()

const ModelStoreProvider = ({ refreshAt, children, ...props }: Props): ReactElement => {
  const clear = useModelStore(state => state.clear)
  const data = createInitialState(props)

  const isInitialized = useStoreReinitializer({
    data,
    getState: useModelStore.getState,
    setState: useModelStore.setState,
    dependency: refreshAt,
    // Keep the user's scene filters when the model is refetched (e.g. after saving a
    // position). createInitialState would otherwise reset them to "everything visible";
    // storeys added since (e.g. by an IFC import) are shown unless the filter was narrowed.
    transformData: (fresh, state) =>
      state.availableStoreys.size
        ? {
            ...fresh,
            ...mergeStoreyFilters(
              storeyFiltersOf(state),
              storeyFiltersOf(fresh as ModelStoreInitialState),
            ),
          }
        : fresh,
  })

  useEffect(() => {
    return () => clear()
  }, [clear])

  if (!isInitialized) return <></>

  return <>{children}</>
}

export { ModelStoreProvider, useModelStore }
export * from './constants'
